import React from 'react';
import { motion } from 'framer-motion';
import { PhoneMissed } from 'lucide-react';
import { MessengerAvatar } from './messengerUtils';

const formatListTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const now = new Date();
  const sameDay = parsed.toDateString() === now.toDateString();
  if (sameDay) {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(parsed);
  }
  const oneDayMs = 86_400_000;
  const diff = now - parsed;
  if (diff < oneDayMs * 6) {
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(parsed);
  }
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(parsed);
};

/**
 * Conversation list row — full-bleed layout with avatar, name, preview,
 * timestamp aligned right and an unread count pill (instead of a raw dot)
 * so users know how many messages are waiting.
 */
export const MessengerListRow = ({
  name,
  preview,
  imageUrl,
  onClick,
  dataTestId,
  unreadCount = 0,
  timestamp = '',
  extraBadge = null,
  previewTone = null,
  rowBadge = null,
}) => {
  const time = formatListTime(timestamp);
  const hasUnread = unreadCount > 0;
  const isMissedCall = previewTone === 'missed_call';
  const displayedPreview = isMissedCall ? 'Missed voice call' : preview;
  const previewColor = isMissedCall
    ? 'var(--status-danger-text, #C4373F)'
    : (hasUnread ? 'var(--text)' : 'var(--text-2)');
  const previewWeight = isMissedCall ? 700 : (hasUnread ? 600 : 500);
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.985 }}
      onClick={onClick}
      data-testid={dataTestId}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 12px',
        cursor: 'pointer',
        textAlign: 'left',
        marginBottom: 4,
        borderRadius: 18,
        position: 'relative',
        background: hasUnread ? 'var(--accent-surface, var(--surface))' : 'transparent',
        border: '1px solid transparent',
        boxShadow: 'none',
      }}
    >
      <MessengerAvatar
        name={name}
        imageUrl={imageUrl}
        size={52}
        textSize={17}
        extraBadge={extraBadge}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'space-between' }}>
          <p
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 16.5,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              flex: 1,
            }}
          >
            {name}
            {rowBadge}
          </p>
          {time ? (
            <span
              data-testid={`${dataTestId}-time`}
              style={{
                fontFamily: "'Inria Serif', serif",
                fontSize: 12,
                color: hasUnread ? 'var(--accent-purple-strong, #6335e0)' : 'var(--text-2)',
                fontWeight: hasUnread ? 700 : 500,
                flexShrink: 0,
              }}
            >
              {time}
            </span>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
          {isMissedCall ? (
            <PhoneMissed
              size={14}
              strokeWidth={2.4}
              color="var(--status-danger-text, #C4373F)"
              data-testid={dataTestId ? `${dataTestId}-missed-icon` : undefined}
              style={{ flexShrink: 0 }}
            />
          ) : null}
          <p
            data-testid={dataTestId ? `${dataTestId}-preview` : undefined}
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 14,
              color: previewColor,
              fontWeight: previewWeight,
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}
          >
            {displayedPreview}
          </p>
          {hasUnread ? (
            <span
              data-testid={`${dataTestId}-unread-badge`}
              style={{
                minWidth: 22,
                height: 22,
                borderRadius: 999,
                background: 'linear-gradient(135deg, var(--accent-purple), var(--accent-purple-strong, #6335e0))',
                color: '#FFFFFF',
                fontFamily: "'Inria Serif', serif",
                fontSize: 12,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 7px',
                boxShadow: '0 6px 12px -6px rgba(112, 63, 246, 0.5)',
                flexShrink: 0,
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </div>
      </div>
    </motion.button>
  );
};
